'use client';

import type { MessageDescriptor } from 'react-intl';
import { useIntl } from 'react-intl';
import { startTransition, useState, type ReactNode } from 'react';
import { InstallmentCalculatorDialog } from '@packages/installment-calculator/src/InstallmentCalculatorDialog/InstallmentCalculatorDialog.client';
import { LegalHintsImage } from '@packages/installment-calculator/src/atoms/LegalHintsImage/LegalHintsImage.client';
import type { FragmentType } from '@packages/gql/generated/shopping';
import type { PaymentTypesFragmentFragmentDoc } from '@packages/gql/generated/shopping/PaymentTypesFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { paymentTypesMessages } from './messages';
import { OutlinedCard } from '../OutlinedCard/OutlinedCard';

/* GraphQL */ `
  fragment PaymentTypesFragment on Product {
    price {
      value
    }
    paymentTypes {
      keys
    }
  }
`;

function getPaymentTypeText(type: string) {
  switch (type) {
    case 'ideal': {
      return paymentTypesMessages.ideal;
    }
    case 'postpay': {
      return paymentTypesMessages.postpay;
    }
    case 'installments': {
      return paymentTypesMessages.installments;
    }
    default: {
      return null;
    }
  }
}

export type PaymentTypesProps = {
  data: FragmentType<typeof PaymentTypesFragmentFragmentDoc>;
};

export const PaymentTypes = ({ data }: PaymentTypesProps) => {
  const { price, paymentTypes } = unmask<typeof PaymentTypesFragmentFragmentDoc>(data);
  const { formatMessage } = useIntl();

  const [dialogOpen, setDialogOpen] = useState(false);

  const texts: MessageDescriptor[] = [];

  paymentTypes?.keys.forEach((key) => {
    const text = getPaymentTypeText(key);
    if (text !== null) {
      texts.push(text);
    }
  });

  if (texts.length === 0) {
    return null;
  }

  return (
    <>
      <OutlinedCard>
        <div className="flex flex-col gap-2">
          <div>
            <div className="text-size-headline-base mb-1.5">
              {formatMessage(paymentTypesMessages.headline)}
            </div>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
              {texts.map((text) => (
                <li key={text.id} className="text-size-body-sm relative pl-1.5">
                  <span className="absolute left-0">•</span>
                  {formatMessage(text)}
                  {text.id === 'paymentTypes.installments' && (
                    <div className="mt-0.5">
                      {formatMessage(paymentTypesMessages.dialog, {
                        link: (msg: ReactNode) => (
                          <button
                            className="cursor-pointer border-none bg-none underline"
                            onClick={() =>
                              startTransition(() => {
                                setDialogOpen(true);
                              })
                            }
                            aria-haspopup="dialog"
                            type="button"
                          >
                            {msg}
                          </button>
                        ),
                      })}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <LegalHintsImage />
        </div>
      </OutlinedCard>
      {dialogOpen && (
        <InstallmentCalculatorDialog
          basePrice={price.value}
          isOpen={dialogOpen}
          onClose={() => startTransition(() => setDialogOpen(false))}
        />
      )}
    </>
  );
};
